import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'

import { STATSIG_EXPERIMENTS } from '@src/config/statsig-experiments.config'
import { useStatsigExperimentBucket } from '@src/experiments/statsig-integration/useStatsigExperiment'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, {
  CUSTOM_FIELD_NAMES,
  getIsWhiteGloveDelivery,
  isProductAlcohol,
  isProductLuggage,
} from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { TrackConfig } from '../../hocs/withTracking'
import { default as Icon } from '../icon'
import { Image } from '../image'
import { default as Modal } from '../modal'

import { ICON_CONSTANTS, ICON_CONSTANTS_MAP } from './icon-constants'
import AgeRestricted from './icons/ageRestricted.inline.svg'
import Installments from './icons/installmentsNew.inline.svg'
import LockNew from './icons/lockNew.inline.svg'
import DeliveryV2 from './icons/order.inline.svg'
import Return from './icons/returnNew.inline.svg'
import * as dsStyles from './product-form-ds.styles'
import stylesV2 from './ProductDescFooterV2.module.scss'
export const ProductDescFooter = ({
  hideFooter,
  isReturnable,
  product,
  selectedOptions,
  isFinalSaleProduct,
  isB2BSelfService,
}: {
  hideFooter: boolean | undefined
  isReturnable: boolean | undefined
  product: Product | undefined
  selectedOptions?: ProductVariantOption[]
  isFinalSaleProduct?: boolean
  isB2BSelfService?: boolean
}) => {
  const { t } = useTranslation('common')
  const [showEasyReturnModal, setShowEasyReturnModal] = useState(false)
  const secondChargingExpBucket = useStatsigExperimentBucket(STATSIG_EXPERIMENTS.SECOND_RETURNS_CHARGING)
  const isInChargingExpBucket = secondChargingExpBucket === 'Show'
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo?.deviceType === DEVICE_TYPES.MOBILE
  const isAgeRestrictedProduct = productHelper?.getIsAgeRestrictedProduct(product as Product)
  const ageLimitInYears = productHelper?.getCustomFieldValue(
    product as Product,
    CUSTOM_FIELD_NAMES.AGE_LIMIT_IN_YEARS,
    false,
    true,
  )
  if (hideFooter) return <></>
  const isWhiteGloveDelivery = getIsWhiteGloveDelivery(product)
  const isLuggageProduct = isProductLuggage(product)

  const easyReturnsTrackConfig: TrackConfig = {
    trackGA: true,
    trackInternal: true,
    // @i18n-ignore
    category: 'checkout',
    // @i18n-ignore
    action: 'easy_returns',
    context: {
      product_id: product?.spid,
      title: product?.title,
      tags: product?.tags,
      subClassId: product?.subClassId,
      color: selectedOptions?.find(option => option.name === 'Color')?.value,
      size: selectedOptions?.find(option => option.name === 'Size')?.value,
    },
  }

  const handleEasyReturnClick = () => {
    setShowEasyReturnModal(prev => !prev)
    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, easyReturnsTrackConfig)
  }

  const legacyStyles = stylesV2
  // DS gate + V2 → Panda twins keyed by the same slot names, so the JSX below stays single-path.
  // The twins mirror the V2 SCSS, so they require `showNewPdpDesignV2` like the sibling DS forks
  // (product-description, product-details-icon*) — gate-on + V1 keeps the V1 SCSS untouched.
  // Legacy rem sizes are authored at the ~10px fluid root and balloon 1.6x under the 16px DS root.
  const finalStyles: Record<string, string | undefined> = shouldUseDesignSystem()
    ? {
        container__info: dsStyles.descFooterDsClass,
        container__info_newDesignV2: undefined,
        container__info__main: dsStyles.descFooterRowDsClass,
        container__info__main__icon: dsStyles.descFooterIconBoxDsClass,
        container__info__main__icon__delivery: dsStyles.descFooterIconDsClass,
        container__info__main__icon__return: dsStyles.descFooterIconDsClass,
        container__info__main__icon__lock: dsStyles.descFooterIconDsClass,
        container__info__main__icon__warranty: dsStyles.descFooterIconDsClass,
        container__info__main__icon__installments: dsStyles.descFooterIconDsClass,
        container__info__main__text: dsStyles.descFooterTextDsClass,
        container__info__main__text__noReturn: dsStyles.descFooterTextDsClass,
        infoTextNewLine: dsStyles.descFooterTextColumnDsClass,
        easyReturns: dsStyles.descFooterEasyReturnsDsClass,
        easyReturnModal: dsStyles.easyReturnModalDsClass,
        easyReturnModalWrapper: dsStyles.easyReturnModalWrapperDsClass,
        modalHeader: dsStyles.easyReturnModalHeaderDsClass,
        subText: dsStyles.easyReturnModalSubTextDsClass,
        redirectLink: dsStyles.easyReturnModalLinkDsClass,
      }
    : legacyStyles

  const paymentOrWarrantyIconUrl = isLuggageProduct
    ? ICON_CONSTANTS_MAP[ICON_CONSTANTS.SHIELD_ICON]
    : ICON_CONSTANTS_MAP[ICON_CONSTANTS.LOCK_ICON]
  const paymentOrWarrantyLabel = isLuggageProduct
    ? isMobile
      ? t('ltd_lifetime_warranty')
      : t('limited_lifetime_warranty')
    : t('secure_payment')
  const paymentOrWarrantyIconClass = isLuggageProduct
    ? finalStyles.container__info__main__icon__warranty
    : finalStyles.container__info__main__icon__lock

  const FreeShippingText = ({ isWhiteGloveDelivery }: { isWhiteGloveDelivery: boolean }) => {
    const { t } = useTranslation('common')
    return (
      <div
        className={classNames(finalStyles.container__info__main__text, {
          [finalStyles.container__whiteGloveTextWidth ?? '']: isWhiteGloveDelivery,
        })}
      >
        {isWhiteGloveDelivery ? t('free_white_glove_delivery') : t('free_standard_shipping')}
      </div>
    )
  }
  return (
    <div
      data-testid="product-desc-footer"
      data-hide-footer={hideFooter?.toString()}
      data-is-returnable={isReturnable?.toString()}
      className={classNames(finalStyles.container__info, finalStyles.container__info_newDesignV2)}
    >
      <div className={finalStyles.container__info__main}>
        <div className={finalStyles.container__info__main__icon}>
          <Icon iconSvg={DeliveryV2} className={finalStyles.container__info__main__icon__delivery} />
        </div>
        <FreeShippingText isWhiteGloveDelivery={!!isWhiteGloveDelivery} />
      </div>
      {isReturnable && !product?.hasRestockingFee ? (
        isFinalSaleProduct ? (
          <div className={finalStyles.container__info__main}>
            <div className={finalStyles.container__info__main__icon}>
              <Image
                url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.INSTALLMENTS_NEW]}
                title={ICON_CONSTANTS.INSTALLMENTS}
                alt=""
                className={finalStyles.container__info__main__icon__installments}
              />
            </div>
            <div className={finalStyles.container__info__main__text}>{t('pay_in_installments')}</div>
          </div>
        ) : !isB2BSelfService ? (
          <div className={finalStyles.container__info__main}>
            <div className={finalStyles.container__info__main__icon}>
              <Icon iconSvg={Return} className={finalStyles.container__info__main__icon__return} />
            </div>
            {isInChargingExpBucket ? (
              <div
                className={classNames(finalStyles.container__info__main__text, finalStyles.easyReturns)}
                onClick={handleEasyReturnClick}
              >
                <p>{t('easy_returns_v2')}</p>
              </div>
            ) : (
              <div className={finalStyles.container__info__main__text}>{t('easy_returns_v2')}</div>
            )}
          </div>
        ) : null
      ) : (
        <div className={finalStyles.container__info__main}>
          {isAgeRestrictedProduct ? (
            <div className={finalStyles.container__info__main__icon}>
              <AgeRestricted className={finalStyles.container__info__main__icon__installments} />
            </div>
          ) : (
            <div className={finalStyles.container__info__main__icon}>
              <Image
                url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.INSTALLMENTS_NEW]}
                title={ICON_CONSTANTS.INSTALLMENTS_NEW}
                alt=""
                className={finalStyles.container__info__main__icon__installments}
              />
            </div>
          )}

          <div
            className={classNames(finalStyles.container__info__main__text, {
              [finalStyles.infoTextNewLine ?? '']: isWhiteGloveDelivery,
            })}
          >
            {isAgeRestrictedProduct
              ? ageLimitInYears
                ? t('age_restricted_product', { ageLimitInYears })
                : t('age_restricted_product_v2')
              : t('pay_in_installments')}
          </div>
        </div>
      )}

      {isB2BSelfService && (
        <div className={finalStyles.container__info__main}>
          <div className={finalStyles.container__info__main__icon}>
            <Icon iconSvg={Installments} className={finalStyles.container__info__main__icon__lock} />
          </div>
          <div
            className={classNames(finalStyles.container__info__main__text, {
              [finalStyles.infoTextNewLine as string]: isWhiteGloveDelivery,
            })}
          >
            <span>{t('pay_in_installments')} </span>
          </div>
        </div>
      )}

      <div className={finalStyles.container__info__main}>
        <div className={finalStyles.container__info__main__icon}>
          {isLuggageProduct ? (
            <Image
              url={paymentOrWarrantyIconUrl}
              title={paymentOrWarrantyLabel}
              alt=""
              className={paymentOrWarrantyIconClass}
            />
          ) : (
            <Icon iconSvg={LockNew} className={paymentOrWarrantyIconClass} />
          )}
        </div>
        <div
          className={classNames(finalStyles.container__info__main__text, {
            [finalStyles.infoTextNewLine ?? '']: isWhiteGloveDelivery,
          })}
        >
          <span>{paymentOrWarrantyLabel}</span>
        </div>
      </div>
      {!isReturnable && isProductAlcohol(product) && (
        <div className={finalStyles.container__info__main}>
          <div className={finalStyles.container__info__main__icon}>
            <Image
              url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.NON_RETURNABLE_BLACK_NEW]}
              title={ICON_CONSTANTS.NON_RETURNABLE_BLACK}
              alt=""
              className={finalStyles.container__info__main__icon__return}
            />
          </div>
          <div className={finalStyles.container__info__main__text}>{t('not_returnable_food_safety')}</div>
        </div>
      )}
      {showEasyReturnModal && isInChargingExpBucket && (
        <Modal
          open={showEasyReturnModal}
          className={finalStyles.easyReturnModal}
          modalWrapperClassName={finalStyles.easyReturnModalWrapper}
          showCloseButton={true}
          onClose={handleEasyReturnClick}
        >
          <div>
            <h3 className={finalStyles.modalHeader}>{t('return_policy')}</h3>
            <div className={finalStyles.subText}>{t('first_return_free_policy')}</div>
            <a
              className={finalStyles.redirectLink}
              // @i18n-ignore
              href={`${window.location.origin}/shipping-returns`}
            >
              {t('learn_more')}
            </a>
          </div>
        </Modal>
      )}
      {!isReturnable && !isAgeRestrictedProduct && (
        <div className={finalStyles.container__info__main}>
          <div className={finalStyles.container__info__main__icon}>
            <Image
              url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.NON_RETURNABLE_BLACK_NEW]}
              title={ICON_CONSTANTS.NON_RETURNABLE_BLACK_NEW}
              alt=""
              className={finalStyles.container__info__main__icon__return}
            />
          </div>
          <div className={finalStyles.container__info__main__text__noReturn}>{t('this_item_is_not_returnable')}</div>
        </div>
      )}
    </div>
  )
}
